import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown } from 'lucide-react';
import { useResizableColumns } from '../hooks/useResizableColumns';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import Icon from './Icon';
import Skeleton from './Skeleton';
import { useDroppedColumns, useFitWidth } from './fitColumns';
import { cx } from './util';

// Row geometry in px — mirrors --row-medium / --row-high / the group header in tokens.css (the virtualizer needs numbers).
const ROW_PX = { medium: 40, high: 32 };
const GROUP_PX = 40;
const HEAD_PX = 32;

/**
 * §2.6 Grid — sticky header, resizable columns (hooks/useResizableColumns), group headers with counts,
 * two densities, keyboard row navigation, hover row actions, row virtualization (@tanstack/react-virtual).
 *
 * columns     [{ key, label, width (px default), render?(row), hide?, edit? }]
 *              hide: true leaves the column out of the view but keeps its stored width (a column a side panel covers)
 *              drop: n (with fit) the order the column leaves in when the Grid is narrower than its columns: 1 first
 *              fill: true the column also takes the Grid's width its siblings leave (its width is then its minimum), so its
 *                    text runs to the Grid's right edge, not to a fixed line short of it; its header keeps its own width
 *                    (TWEAK-1: Scripts' Characters)
 *              edit: inline edit for the cell (HD-7): { value(row) → string, onCommit(row, next), editable?(row) → bool,
 *                    label(row) → accessible name, placeholder?, multiline? }. The cell shows render(row) (or the value)
 *                    as a button; click or Enter opens an input (a one-row textarea when multiline, Shift+Enter for a new
 *                    line); Enter or leaving the field commits, Escape cancels. onCommit runs only when the text changed.
 *                    A row where editable(row) is false shows render(row) as usual.
 * rows        records; rowKey(row) gives the id
 * groups      optional [{ key, header (node) }] in display order; groupOf(row) → group key
 * density     'medium' (40px, t-body — Console default) | 'high' (32px, t-grid — Live, Edit queue)
 * selectedKey highlighted row (accent-tint)
 * onOpen(row) row click, Enter or Space (opens the Expanded-row panel)
 * rowActions(row) hover / focus actions (buttons)
 * mutedRow(row)   Backburner treatment (ink-3)
 * storageKey  persists column widths
 * state       'data' | 'loading' | 'error';  empty / error: props for EmptyState / ErrorState
 * autoRows    true: a row grows to its content (a cell that wraps) and is measured; false (default): every row is one
 *             fixed line (HF-FIX-1b: Live's Talent column expanded)
 * fit         drop columns until the shown widths fit the Grid's width (ui/fitColumns.js): true always (Storage), 'auto'
 *             (the default) at <= 1024 px only, false never. With `drop` numbers only those columns leave, in that order;
 *             without, columns leave from the right, never the first and never one marked `keep`.
 */
export default function Grid({
  columns, rows, rowKey = r => r.id, groups, groupOf, density = 'medium', selectedKey, onOpen, rowActions, mutedRow,
  storageKey, label, state = 'data', empty, error, height = '100%', fit = 'auto', autoRows = false,
}) {
  const { widths, startDrag } = useResizableColumns(storageKey || `ui-grid:${columns.map(c => c.key).join(',')}`, columns.map(c => c.width || 160));
  const scrollRef = useRef(null);
  // fit: the Grid's own width (its clientWidth: the scroller, without a vertical scrollbar) and the columns that leave.
  const avail = useFitWidth(scrollRef, fit);
  const dropped = useDroppedColumns(columns, widths, avail);
  // Hidden (and dropped) columns keep their slot in the width store (so a stored layout survives), but take no track.
  const shown = columns.map((c, i) => ({ c, i })).filter(({ c, i }) => !c.hide && !dropped.has(i));
  const template = shown.map(({ c, i }) => (c.fill ? `minmax(${widths[i]}px, 1fr)` : `${widths[i]}px`)).join(' ');
  const [editing, setEditing] = useState(null);   // { key, col, value } — the one cell being edited inline
  const [collapsed, setCollapsed] = useState(() => new Set());
  const rowPx = ROW_PX[density];

  // Flatten groups + rows into one virtual list.
  const items = useMemo(() => {
    if (!groups || !groupOf) return rows.map(r => ({ type: 'row', row: r }));
    const by = new Map(groups.map(g => [g.key, []]));
    for (const r of rows) by.get(groupOf(r))?.push(r);
    const out = [];
    for (const g of groups) {
      const rs = by.get(g.key) || [];
      out.push({ type: 'group', group: g, count: rs.length });
      if (!collapsed.has(g.key)) for (const r of rs) out.push({ type: 'row', row: r });
    }
    return out;
  }, [rows, groups, groupOf, collapsed]);
  const rowIndexes = useMemo(() => items.map((it, i) => (it.type === 'row' ? i : -1)).filter(i => i >= 0), [items]);

  const size = useCallback(i => (items[i].type === 'group' ? GROUP_PX : rowPx), [items, rowPx]);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: size,
    overscan: 12,
    scrollMargin: HEAD_PX,                       // the sticky column header sits above the body
    scrollPaddingStart: groups ? GROUP_PX : 0,   // keyboard-focused rows land below the sticky group header
  });
  useEffect(() => { virtualizer.measure(); }, [size, autoRows, virtualizer]);
  const virtual = virtualizer.getVirtualItems();

  // Sticky group header: the last group header whose top has scrolled under the column header.
  const fixedOffsets = useMemo(() => { let y = 0; return items.map((_, i) => { const o = y; y += size(i); return o; }); }, [items, size]);
  // Measured rows: the virtualizer's own starts (they include the header's scroll margin).
  const offsets = autoRows ? virtualizer.measurementsCache.map(m => m.start - HEAD_PX) : fixedOffsets;
  const scrolled = virtualizer.scrollOffset ?? 0;
  let activeGroup = -1;
  for (let i = 0; i < items.length && offsets[i] <= scrolled; i++) if (items[i].type === 'group') activeGroup = i;

  // Keyboard row navigation (roving tabindex across the virtual window).
  const [focusIdx, setFocusIdx] = useState(null);
  const focusRow = useCallback(idx => {
    setFocusIdx(idx);
    virtualizer.scrollToIndex(idx, { align: 'auto' });
    requestAnimationFrame(() => scrollRef.current?.querySelector(`[data-index="${idx}"]`)?.focus());
  }, [virtualizer]);
  const tabbable = focusIdx ?? (selectedKey != null ? items.findIndex(it => it.type === 'row' && rowKey(it.row) === selectedKey) : rowIndexes[0]);
  const onRowKey = (e, idx, row) => {
    const pos = rowIndexes.indexOf(idx);
    if (e.key === 'ArrowDown' && pos < rowIndexes.length - 1) { e.preventDefault(); focusRow(rowIndexes[pos + 1]); }
    else if (e.key === 'ArrowUp' && pos > 0) { e.preventDefault(); focusRow(rowIndexes[pos - 1]); }
    else if (e.key === 'Home') { e.preventDefault(); focusRow(rowIndexes[0]); }
    else if (e.key === 'End') { e.preventDefault(); focusRow(rowIndexes[rowIndexes.length - 1]); }
    else if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onOpen?.(row); }
  };
  const toggleGroup = key => setCollapsed(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const gridStyle = { gridTemplateColumns: template };
  const groupHeader = (it, sticky) => (
    <div className={cx('ui-grid__group', sticky && 'ui-grid__group--sticky')} role="row" aria-hidden={sticky || undefined}>
      <button type="button" className="ui-grid__group-btn" aria-expanded={!collapsed.has(it.group.key)} tabIndex={sticky ? -1 : 0}
        onClick={() => toggleGroup(it.group.key)}>
        <Icon icon={ChevronDown} />{it.group.header}<span className="ui-meta">{it.count}</span>
      </button>
    </div>
  );

  return (
    <div className={cx('ui ui-grid', `ui-grid--${density}`, autoRows && 'ui-grid--auto')} ref={scrollRef} style={{ height }} role="grid" aria-label={label} aria-rowcount={rows.length}>
      <div className="ui-grid__head" role="row" style={gridStyle}>
        {shown.map(({ c, i }, n) => (
          // A fill column's header stays at its own width: the room it takes is for the cells' text, not the header's field.
          <div key={c.key} className="ui-grid__th" role="columnheader" style={c.fill ? { maxWidth: widths[i] } : undefined}>
            {c.label}
            {n < shown.length - 1 && <span className="ui-grid__resize" aria-hidden="true" onMouseDown={e => startDrag(i, e)} />}
          </div>
        ))}
      </div>
      {state === 'loading' && Array.from({ length: 10 }, (_, i) => (
        <div key={i} className="ui-grid__row ui-grid__row--static" style={gridStyle}>
          {shown.map(({ c }) => <span key={c.key} className="ui-grid__cell"><Skeleton width="70%" /></span>)}
        </div>
      ))}
      {state === 'error' && <ErrorState {...error} />}
      {state === 'data' && rows.length === 0 && <EmptyState {...empty} />}
      {state === 'data' && rows.length > 0 && (
        <>
          {activeGroup >= 0 && offsets[activeGroup] < scrolled && groupHeader(items[activeGroup], true)}
          <div className="ui-grid__body" style={{ height: virtualizer.getTotalSize() }}>
            {virtual.map(v => {
              const it = items[v.index];
              const pos = { transform: `translateY(${v.start - HEAD_PX}px)`, top: 0 };
              if (it.type === 'group') return <div key={v.key} className="ui-grid__vitem" style={pos}>{groupHeader(it, false)}</div>;
              const k = rowKey(it.row);
              return (
                <div key={v.key} ref={autoRows ? virtualizer.measureElement : undefined} data-index={v.index} data-key={k} role="row" tabIndex={v.index === tabbable ? 0 : -1}
                  className={cx('ui-grid__row', mutedRow?.(it.row) && 'ui-grid__row--muted')} aria-selected={k === selectedKey}
                  style={{ ...gridStyle, ...pos }}
                  onClick={() => { setFocusIdx(v.index); onOpen?.(it.row); }} onKeyDown={e => onRowKey(e, v.index, it.row)}>
                  {shown.map(({ c }) => (
                    <span key={c.key} className="ui-grid__cell" role="gridcell">
                      {c.edit && (!c.edit.editable || c.edit.editable(it.row))
                        ? <EditCell col={c} row={it.row} draft={editing && editing.key === k && editing.col === c.key ? editing.value : null}
                            onStart={() => setEditing({ key: k, col: c.key, value: c.edit.value(it.row) ?? '' })}
                            onDraft={value => setEditing(e => (e ? { ...e, value } : e))}
                            onEnd={() => setEditing(null)} />
                        : c.render ? c.render(it.row) : it.row[c.key]}
                    </span>
                  ))}
                  {rowActions && <span className="ui-grid__actions" onClick={e => e.stopPropagation()}>{rowActions(it.row)}</span>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// One inline-editable cell (see `edit` above): a button at rest, an input while editing. `draft` is the text being
// edited (null at rest); the Grid holds it, so it survives the row scrolling out of the virtual window and back.
function EditCell({ col, row, draft, onStart, onDraft, onEnd }) {
  const { value, onCommit, label, placeholder, multiline } = col.edit;
  const finished = useRef(false);   // committed or cancelled: the blur that follows must not commit again
  const stop = e => e.stopPropagation();
  if (draft == null) {
    const shownValue = col.render ? col.render(row) : value(row);
    const empty = shownValue == null || shownValue === '';
    return (
      <button type="button" className="ui-grid__edit" aria-label={label?.(row)} data-empty={empty || undefined}
        onClick={e => { e.stopPropagation(); finished.current = false; onStart(); }}>
        {empty ? placeholder : shownValue}
      </button>
    );
  }
  const Field = multiline ? 'textarea' : 'input';
  const commit = () => {
    if (finished.current) return;
    finished.current = true;
    const before = value(row) ?? '';
    onEnd();
    if (draft !== before) onCommit(row, draft);
  };
  return (
    <Field autoFocus className="ui-input ui-grid__editor" rows={multiline ? 1 : undefined} aria-label={label?.(row)} value={draft}
      placeholder={placeholder} onChange={e => onDraft(e.target.value)} onClick={stop} onMouseDown={stop} onBlur={commit}
      onKeyDown={e => {
        e.stopPropagation();
        if (e.key === 'Escape') { e.preventDefault(); finished.current = true; onEnd(); }
        else if (e.key === 'Enter' && !(multiline && e.shiftKey)) { e.preventDefault(); commit(); }
      }} />
  );
}
